import * as React from 'react';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';

import {ErrorState} from '../error-state/error-state';
import {LoadingPage} from '../loading-page/loading-page';

import {FetchMoreButton} from './fetch-more-button';
import {Table} from './table';
import type {Column} from './table-types';
import type {SpecialState} from './types';

import styles from './display-only-table.module.css';

type Props = {
    columns: Column<any>[];
    data: any[];
    isLoading: boolean;
    isFirstLoad?: boolean;
    error: string | null | undefined;
    emptyStateText?: string;
    hasNextPage?: boolean;
    onFetchMoreRows?: () => void;
    fetchMoreRowsNumberText?: string;
    disableSorting?: boolean;
    fullWidth?: boolean; // Sets table to full width, proportionally increasing the width of each column,
    extendToFitWidth?: boolean; // Adds an empty cell to the end of each row that extends to fill the remaining width, leaving set column widths as-is,
    specialStates?: SpecialState[];
    isResizable?: boolean;
    sortBy?: any[];
    shouldShowFooter?: boolean;
    displayFooterAtTop?: boolean;
    isFullContainerWidth?: boolean; // If true, the table has no padding around it and doesn't need top and left header borders,
    emptyStateSubtitle?: React.ReactNode | string;
    emptyStateIcon?: React.ReactNode;
    renderRowActionMenu?: () => React.ReactElement;
    isStriped?: boolean;
    emptyStateCustomContent?: React.ReactNode;
    hasCellBorders?: boolean;
    hasFooterCellBorders?: boolean;
    shouldFooterAlign?: boolean;
};

export function DisplayOnlyTable(props: Props) {
    const {
        disableSorting = true,
        extendToFitWidth,
        fullWidth = true,
        isFullContainerWidth,
        specialStates = [],
    } = props;

    const defaultSpecialStates = [
        {
            shouldRender: () => Boolean(props.error),
            component: <ErrorState errorMessage={props.error} />,
        },
        {
            shouldRender: () => props.isLoading,
            component: (
                <div className={styles['loading-container']}>
                    <LoadingPage padding={4} />
                </div>
            ),
        },
        {
            shouldRender: () => !props.error && !props.data.length && !props.isLoading,
            component: (
                <>
                    {props.emptyStateCustomContent ? (
                        props.emptyStateCustomContent
                    ) : (
                        <NothingFoundEmptyState
                            title={props.emptyStateText || 'Nothing found'}
                            subtitle={props.emptyStateSubtitle}
                            icon={props.emptyStateIcon}
                        />
                    )}
                </>
            ),
        },
    ];

    // Merge any special states passed in (first) with the default special states (second)
    // so passed ones are rendered first if they exist
    const tableSpecialStates = [...specialStates, ...defaultSpecialStates];

    return (
        <div className={styles['table-container']}>
            <Table
                isResizable={props.isResizable}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHeaderLeftBorder={!isFullContainerWidth}
                hasHeaderTopBorder={!isFullContainerWidth}
                hasHoverState={false}
                isCompact={true}
                hasBorders={true}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={disableSorting}
                fullWidth={fullWidth}
                extendToFitWidth={extendToFitWidth}
                sortBy={props.sortBy}
                shouldShowFooter={props.shouldShowFooter}
                displayFooterAtTop={props.displayFooterAtTop}
                shouldFooterAlign={props.shouldFooterAlign}
                // Removes "triple sort" and forces there
                // always to be a asc/desc sort applied
                disableSortRemove={true}
                shouldPreventSelectAll={false}
                columns={props.columns}
                // Switching any filters should show the loading state with no data
                data={props.isFirstLoad ? [] : props.data}
                specialStates={tableSpecialStates}
                renderRowActionMenu={props.renderRowActionMenu}
                isStriped={props.isStriped}
                hasCellBorders={props.hasCellBorders}
                hasFooterCellBorders={props.hasFooterCellBorders}
            />
            {props.onFetchMoreRows && props.hasNextPage && !props.isLoading ? (
                <FetchMoreButton
                    buttonText={`+ Load ${props.fetchMoreRowsNumberText || '50'} more rows`}
                    onClick={props.onFetchMoreRows}
                />
            ) : undefined}
        </div>
    );
}
